<template>
    <div class="parental-gate">
        <h1 class="title">Parent, enter your birth year</h1>

        <div class="birth-year-section">
            <div class="number-pad">
                <div class="year-input">
                    <input
                        v-for="(digit, index) in 4"
                        :key="index"
                        ref="digitInputs"
                        v-model="yearDigits[index]"
                        type="text"
                        maxlength="1"
                        :data-index="index"
                        :readonly="isMobileDevice"
                        @focus="isMobileDevice ? preventKeyboard($event) : null"
                        @input="handleDigitInput($event, index)"
                        @keydown="handleKeyDown($event, index)"
                    />
                </div>
                <button class="number-pad-btn delete-btn" @click="deleteDigit">
                    <i class="pi pi-delete-left" />
                </button>

                <!--next row-->
                <button
                    v-for="num in [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]"
                    :key="num"
                    class="number-pad-btn"
                    @click="appendDigit(num)"
                >
                    {{ num }}
                </button>
            </div>

            <div v-if="showError" class="error-message">
                Try again! Get a grown up to help you.
            </div>

            <ButtonMedium
                v-if="isYearComplete"
                text="Continue"
                class="continue-button"
                :class="{ active: isYearComplete }"
                @click="validateYear"
            />
        </div>
    </div>
</template>

<script>
import {
    EVENTS,
    TrackingService,
} from '@/core/services/TrackingService/TrackingService';
import { MobileScreenWidthDetect } from '@/core/mixins/MobileScreenWidthDetect.js';
import ButtonMedium from '@/student/ui/button/ButtonMedium.vue';
export default {
    components: { ButtonMedium },
    mixins: [MobileScreenWidthDetect],
    emits: ['gate-passed'],
    data() {
        return {
            yearDigits: ['', '', '', ''],
            minYear: 1925, // 100 years old max
            maxYear: 2007, // 18 years old min
            showError: false,
            activeInputIndex: 0,
        };
    },
    computed: {
        fullYear() {
            return this.yearDigits.join('');
        },
        isYearComplete() {
            return this.yearDigits.every((digit) => digit !== '');
        },
    },
    mounted() {
        // Focus first input on mount
        this.$nextTick(() => {
            this.focusInput(0);
        });
        new TrackingService().track(EVENTS.SEES_PARENT_BIRTH_YEAR_SCREEN);
    },
    methods: {
        handleDigitInput(event, index) {
            const value = event.target.value;

            // Ensure we only have numeric input
            if (!/^\d*$/.test(value)) {
                this.yearDigits[index] = '';
                return;
            }

            // If we have a digit, move to next input
            if (value && index < 3) {
                this.focusInput(index + 1);
            }

            // Hide any previous error
            this.showError = false;
        },

        handleKeyDown(event, index) {
            // Handle backspace
            if (event.key === 'Backspace') {
                if (this.yearDigits[index] === '' && index > 0) {
                    this.focusInput(index - 1);
                }
            }
        },

        focusInput(index) {
            this.activeInputIndex = index;
            this.$nextTick(() => {
                if (this.$refs.digitInputs && this.$refs.digitInputs[index]) {
                    this.$refs.digitInputs[index].focus();
                }
            });
        },

        preventKeyboard(event) {
            // event.target.blur();
            this.activeInputIndex = parseInt(event.target.dataset.index || 0);
        },

        appendDigit(digit) {
            // Find the first empty input
            const emptyIndex = this.yearDigits.findIndex((d) => d === '');

            if (emptyIndex !== -1) {
                this.yearDigits[emptyIndex] = digit.toString();

                // Move to next input if available
                if (emptyIndex < 3) {
                    this.focusInput(emptyIndex + 1);
                }
            }

            // Hide any previous error
            this.showError = false;
        },

        deleteDigit() {
            // Find the last non-empty input
            let lastFilledIndex = 3;

            while (
                lastFilledIndex >= 0 &&
                this.yearDigits[lastFilledIndex] === ''
            ) {
                lastFilledIndex--;
            }

            if (lastFilledIndex >= 0) {
                this.yearDigits[lastFilledIndex] = '';
                this.focusInput(lastFilledIndex);
            }

            // Hide any previous error
            this.showError = false;
        },

        validateYear() {
            const year = parseInt(this.fullYear, 10);

            new TrackingService().track(
                EVENTS.ENTERED_PARENT_BIRTH_YEAR,
                this.fullYear,
            );
            if (year >= this.minYear && year <= this.maxYear) {
                this.onSuccess();
            } else {
                this.showError = true;
            }
        },

        onSuccess() {
            this.$emit('gate-passed', this.fullYear);
        },
    },
};
</script>

<style scoped lang="scss">
.parental-gate {
    font-family: $secondary-font-family;
}
.title {
    font-family: $secondary-font-family;
    text-align: center;
    font-size: 24px;
    font-style: italic;
    margin-top: 20px;
    margin-bottom: 20px;
    color: white;
}

.birth-year-section {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.label {
    margin-bottom: 16px;
    font-size: 16px;
}

.year-input {
    grid-column: span 4;
    display: flex;
    gap: 8px;
    width: 100%;
    max-width: 300px;
    margin-bottom: 20px;
}

.year-input input {
    font-family: $secondary-font-family;
    width: 45px;
    height: 45px;
    border: 2px solid white;
    background-color: white;
    border-radius: 8px;
    text-align: center;
    font-size: 24px;
    font-weight: bold;
    color: #333;
    caret-color: transparent;
}

.number-pad {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    max-width: 300px;
    margin-bottom: 20px;
}

.number-pad-btn {
    font-family: $secondary-font-family;
    max-width: 52px;
    background-color: #b686f6;
    border: none;
    color: white;
    text-shadow: 0 1px black;
    box-shadow:
        0 2px 2px 0 rgba(0, 0, 0, 0.25),
        inset 0 1px rgba(255, 255, 255, 0.2),
        inset 0 -1px rgba(0, 0, 0, 0.2);
    font-size: 24px;
    font-weight: bold;
    height: 45px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.number-pad-btn:hover {
    background-color: rgba(181, 132, 245, 0.75);
}

.delete-btn {
    --p-icon-size: 20px;
}

.error-message {
    color: #ff4d4d;
    background-color: rgba(255, 255, 255, 0.9);
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: bold;
    margin-bottom: 16px;
}

.continue-button {
    text-transform: uppercase;
}
</style>
